import AxiosService from "@/services/AxiosService";

// Resolves with the API envelope ({ status, type, message, data }) for both successful and failed requests.
const handle = (request) =>
  request
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      console.log(error);
      if (!error.response) return undefined;
      // A proxy error (e.g. 413 of nginx for a too big upload) has no API envelope
      const data = error.response.data;
      return data && typeof data === "object" ? data : { status: error.response.status, type: "Error", data: {} };
    });

// Placement of a domain: { mode: "all" } | { mode: "selected", group_ids: [id], satellite_ids: [id of a satellite without a group] }.
// Domain: { id, host, router: { id, name } | null, placement: { mode, groups, satellites }, satellites_count, certificate, created_at }.
class SatelliteService {
  // Satellites

  getSatellites() {
    return handle(AxiosService.get("/satellites"));
  }

  createSatellite() {
    return handle(AxiosService.post("/satellites"));
  }

  // data: { satellite, settings_owner: "group" | "satellite", settings: { key: value }, domains }
  getSatellite(id) {
    return handle(AxiosService.get(`/satellites/${id}`));
  }

  // changes: { name?, group_id?, settings? }; settings only for a satellite without a group. Responds like getSatellite.
  updateSatellite(id, changes) {
    return handle(AxiosService.patch(`/satellites/${id}`, changes));
  }

  deleteSatellite(id) {
    return handle(AxiosService.delete(`/satellites/${id}`));
  }

  // Asks the agent to update calypso to the latest release. Responds with the satellite as in getSatellites;
  // 422 with data.reason (not_installed, offline, up_to_date, manual_update_required), 503 if the latest release is unknown.
  requestAgentUpdate(id) {
    return handle(AxiosService.post(`/satellites/${id}/update`));
  }

  // Moves satellites to a group, null takes them out of their groups; all or nothing.
  moveSatellites(ids, groupId) {
    return handle(AxiosService.post("/satellites/bulk", { ids, group_id: groupId }));
  }

  getSettingsSchema() {
    return handle(AxiosService.get("/satellite-settings/schema"));
  }

  // Groups

  // data: [{ id, name, members_count, domains_count, overrides, created_at, updated_at }]
  getGroups() {
    return handle(AxiosService.get("/satellite-groups"));
  }

  // body: { name, settings?, satellite_ids? }; 201 with the same data as getGroup, 409 if the limit is reached.
  createGroup(body) {
    return handle(AxiosService.post("/satellite-groups", body, { expectCreated: true }));
  }

  // data: { group, settings: { key: value }, satellites, domains }
  getGroup(id) {
    return handle(AxiosService.get(`/satellite-groups/${id}`));
  }

  // body: { name?, settings?, satellite_ids? (all members) }; responds like getGroup.
  updateGroup(id, body) {
    return handle(AxiosService.patch(`/satellite-groups/${id}`, body));
  }

  // moveTo: id of the group the satellites move to; without it they stay without a group with a copy of its settings and domains.
  deleteGroup(id, moveTo = null) {
    return handle(AxiosService.delete(`/satellite-groups/${id}`, { params: moveTo ? { move_to: moveTo } : {} }));
  }

  // Landings

  // data: { landings, count, limit, max_archive_size }
  getLandings() {
    return handle(AxiosService.get("/satellite-landings"));
  }

  // name is optional: the backend uses the file name without the extension. 201 on success, 409 if the limit is reached.
  uploadLanding(file, name) {
    const formData = new FormData();
    formData.append("file", file);
    if (name) formData.append("name", name);
    return handle(AxiosService.post("/satellite-landings", formData, { expectCreated: true }));
  }

  renameLanding(id, name) {
    return handle(AxiosService.patch(`/satellite-landings/${id}`, { name }));
  }

  // Keeps the id, so the satellites using the landing get the new archive.
  replaceLandingArchive(id, file) {
    const formData = new FormData();
    formData.append("file", file);
    return handle(AxiosService.post(`/satellite-landings/${id}/archive`, formData, { expectCreated: true }));
  }

  // 409 with data.used_by: { routers } if the landing is used by routers.
  deleteLanding(id) {
    return handle(AxiosService.delete(`/satellite-landings/${id}`));
  }

  // Platforms: own landing platforms, the only ones the routers can use. They are platforms of the platforms section too.

  // data: { platforms: [{ id, name, url, created_at }], count }, newest first
  getPlatforms() {
    return handle(AxiosService.get("/satellite-platforms"));
  }

  // body: { name, category_id: 1 (adult) | 2 (mainstream) }; 201 with data.platform, 422 with violations keyed by "name" or "category_id".
  createPlatform(body) {
    return handle(AxiosService.post("/satellite-platforms", body, { expectCreated: true }));
  }

  // Routers

  // data: { routers: [{ ..., used_by: { domains: [{ id, host }] } }], count, limit }
  getRouters() {
    return handle(AxiosService.get("/satellite-routers"));
  }

  // data: { platforms: [{ id, name }], landings: [{ id, name }], defaults: { platform_param, sub_params, strategy }, limits: { routes, landings_per_route, path_length } }
  getRouterOptions() {
    return handle(AxiosService.get("/satellite-routers/options"));
  }

  // body: { name, routes }; 201 on success, 409 if the limit is reached, 422 with violations keyed by "name", "routes" or "routes.{index}.{field}".
  createRouter(body) {
    return handle(AxiosService.post("/satellite-routers", body, { expectCreated: true }));
  }

  // body: { name?, routes? }; routes replace all the routes of the router.
  updateRouter(id, body) {
    return handle(AxiosService.patch(`/satellite-routers/${id}`, body));
  }

  // The domains of the router are left without a router.
  deleteRouter(id) {
    return handle(AxiosService.delete(`/satellite-routers/${id}`));
  }

  // Domains

  // data: { domains, count, limit }, sorted by host
  getDomains() {
    return handle(AxiosService.get("/satellite-domains"));
  }

  // body: { hosts: [string] (1..100), router_id?, placement? }. 201 with data: { domains, skipped }, 409 with data.limit,
  // 422 with violations keyed by "hosts", "hosts.{index}", "router_id" or "placement*"; all or nothing.
  addDomains(body) {
    return handle(AxiosService.post("/satellite-domains", body, { expectCreated: true }));
  }

  // changes: { router_id?, placement? }; data: { domain }
  updateDomain(id, changes) {
    return handle(AxiosService.patch(`/satellite-domains/${id}`, changes));
  }

  // changes: { router_id?, placement? } for all the domains; data: { domains }
  updateDomains(ids, changes) {
    return handle(AxiosService.post("/satellite-domains/bulk", { ids, ...changes }));
  }

  deleteDomains(ids) {
    return handle(AxiosService.post("/satellite-domains/bulk", { ids, delete: true }));
  }

  // data: { domain, router: { id, name, routes: [{ path, platform, sub_params, strategy, landings: [{ id, name }] }] } | null,
  // satellites: [satellite + in_dns], dns: { resolved, records: [{ ip, satellite, serves }] } }
  getDomainTraffic(id) {
    return handle(AxiosService.get(`/satellite-domains/${id}/traffic`));
  }
}
export default new SatelliteService();
